import { DEFAULT_LANG_CODE, LANGUAGE } from '@/commons/constants';
import { LanguageType } from '@/types';
import { Checkbox, Divider, Form, Input, InputNumber, Rate, Select, Switch, Tabs } from 'antd';
import { ColProps } from 'antd/lib/col';
import { Rule } from 'antd/lib/form';
import { FC } from 'react';

import AsyncSelect from '../AsyncSelect';
import FileUploaderModalField from '../FileUploaderModalField';
import MarkdownField from '../MarkdownField';

interface FieldValueType {
  component?: keyof typeof componentMapping;
  label?: string;
  rules?: Rule[];
  name: string | (string | number)[];
  propsComponent?: any;
  propsFormItem?: any;
  renderer?: (props: any) => any;
  isList?: boolean;
  order?: number;
  componentItem?: any;
  propsCol?: ColProps;
  disabled?: boolean;
}

export interface ItemFieldType {
  [name: string]: FieldValueType;
}

export interface FieldsRenderType {
  field?: {
    name: number | string;
    key: number | string;
    fieldKey: number | string;
  };
  itemFields: ItemFieldType;
}

export interface ModelTranslationsType {
  languages?: LanguageType[];
  itemFields: ItemFieldType;
  translateFields?: ItemFieldType;
  destroyOnHidden?: boolean;
  tabBarActiveKey?: string;
  onTabClick?: (key: string) => void;
}

export const componentMapping: { [key: string]: any } = {
  input: Input,
  textarea: Input.TextArea,
  checkbox: Checkbox,
  switch: Switch,
  rate: Rate,
  select: Select,
  number: InputNumber,
  divider: Divider,
  upload: FileUploaderModalField,
  asyncSelect: AsyncSelect,
  markdown: MarkdownField,
};

export const compareIFieldValues = (a: [string, FieldValueType], b: [string, FieldValueType]) => {
  const order1 = a[1].order;
  const order2 = b[1].order;
  if (order1 === undefined && order2 === undefined) {
    return 0;
  }

  if (order1 === undefined) {
    return 1;
  }

  if (order2 === undefined) {
    return -1;
  }

  return order1 - order2;
};

export const FieldRender: FC<FieldValueType> = (props: FieldValueType) => {
  const {
    component,
    label,
    rules = [],
    name,
    propsComponent = {},
    propsFormItem = {},
    renderer,
    isList,
    componentItem,
  } = props;
  const customRules = handleAddRules(rules);
  const Component = component ? componentMapping[component] : componentItem;
  const formItemProps = {
    label: label,
    name: name,
    rules: customRules || [],
    ...propsFormItem,
  };

  if (isList) {
    return (
      <Form.List name={name} rules={customRules || []} {...propsFormItem}>
        {(fields, operation) =>
          renderer ? (
            renderer({ label, fields, operation, propsComponent })
          ) : (
            <Component label={label} fields={fields} operation={operation} {...propsComponent} />
          )
        }
      </Form.List>
    );
  }

  return (
    <Form.Item {...formItemProps}>
      {renderer ? renderer({ ...propsComponent }) : <Component {...propsComponent} />}
    </Form.Item>
  );
};

export const FieldsRender: FC<FieldsRenderType> = (props: FieldsRenderType) => {
  const { field, itemFields } = props;
  const entries = Object.entries(itemFields).sort(compareIFieldValues);

  if (!field) {
    return (
      <>
        {entries.map(([fieldName, fieldComponent]) => {
          return (
            <FieldRender
              key={fieldName}
              {...fieldComponent}
              name={fieldName}
              propsFormItem={{
                ...(fieldComponent.propsFormItem || {}),
              }}
            />
          );
        })}
      </>
    );
  }

  return (
    <>
      {entries.map(([fieldName, fieldComponent]) => {
        return (
          <FieldRender
            key={`${field.key}-${fieldName}`}
            {...fieldComponent}
            name={[field.name, fieldName]}
            propsFormItem={{
              ...(fieldComponent.propsFormItem || {}),
              field: field,
              fieldKey: [field.fieldKey, fieldName],
            }}
          />
        );
      })}
    </>
  );
};

export const ModelTranslations: FC<ModelTranslationsType> = (props: ModelTranslationsType) => {
  const {
    itemFields,
    translateFields,
    languages = Object.values(LANGUAGE),
    destroyOnHidden = false,
    tabBarActiveKey = LANGUAGE[DEFAULT_LANG_CODE].code,
    onTabClick,
  } = props;
  let tabsProps = {};
  if (typeof onTabClick === 'function') tabsProps = { activeKey: tabBarActiveKey, onTabClick };
  return (
    <Tabs
      type="card"
      destroyOnHidden={destroyOnHidden}
      items={languages.map((language) => ({
        key: language.code,
        label: language.name,
        forceRender: true,
        children: (
          <FieldsRender
            field={{
              key: language.code,
              name: language.code,
              fieldKey: language.code,
            }}
            itemFields={
              language.code === DEFAULT_LANG_CODE ? itemFields : translateFields || itemFields
            }
          />
        ),
      }))}
      {...tabsProps}
    />
  );
};

const handleAddRules = (rules: Rule[]) => {
  return rules;
};
